import * as React from "react"
import { Link, router } from "@inertiajs/react"
import { ArrowLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { formatDateOnly, formatDay } from "@/lib/dates"
import { formatPence } from "@/lib/money"
import { cn } from "@/lib/utils"
import type { AccountDesign, AccountOrderSummary, BottleType } from "@/types/inertia"
import "@/styles/public-order.css"
import "@/styles/public-account.css"

// Shared pieces of the customer account pages (M6): the compact hero, a
// design card with its three actions, an order row and the empty state.
// Styling: public-account.css (and public-order.css for .ord-empty).

/** "Order #12" for an order enquiry, "Enquiry #12" for a consultation (as in the mails). */
export function orderTitle(order: Pick<AccountOrderSummary, "id" | "enquiry_type">) {
  return `${order.enquiry_type === "order" ? "Order" : "Enquiry"} #${order.id}`
}

export function bottlesText(count: number) {
  return `${count} ${count === 1 ? "bottle" : "bottles"}`
}

/** The compact account hero: an optional way back, the eyebrow, the h1 and a lede (the flash is PublicShell's, M7). */
export function AccountHero({
  eyebrow,
  title,
  lede,
  back,
  actions,
  titleId,
}: {
  eyebrow: string
  title: React.ReactNode
  lede?: React.ReactNode
  back?: { href: string; label: string }
  actions?: React.ReactNode
  titleId?: string
}) {
  return (
    <section className="hero hero-compact acct-hero" aria-labelledby={titleId}>
      <div className="hero-glow" aria-hidden="true" />
      <div className="hero-inner">
        <div className="shell">
          <div className="hero-copy">
            {back && (
              <Link href={back.href} className="acct-back">
                <ArrowLeft aria-hidden="true" />
                {back.label}
              </Link>
            )}
            <div className="eyebrow">{eyebrow}</div>
            <h1 id={titleId}>{title}</h1>
            {lede && <p className="lede">{lede}</p>}
            {actions && <div className="hero-actions mt-9">{actions}</div>}
          </div>
        </div>
      </div>
    </section>
  )
}

/** A section head: the h2 (with its count), and "See all" when there is a list page. */
export function SectionHead({
  id,
  title,
  count,
  seeAll,
}: {
  id: string
  title: string
  count?: number
  seeAll?: { href: string; label: string }
}) {
  return (
    <div className="acct-section-head">
      <h2 id={id}>
        {title}
        {count !== undefined && count > 0 && <span className="acct-count">({count})</span>}
      </h2>
      {seeAll && (
        <Link href={seeAll.href} className="acct-see-all">
          See all<span className="sr-only"> {seeAll.label}</span>
        </Link>
      )}
    </div>
  )
}

export function EmptyState({
  eyebrow,
  title,
  children,
  action,
  level = 3,
}: {
  eyebrow: string
  title: string
  children: React.ReactNode
  action: { href: string; label: string }
  /** 2 on a list page (straight under the h1), 3 inside an overview section. */
  level?: 2 | 3
}) {
  const Heading = level === 2 ? "h2" : "h3"
  return (
    <div className="ord-empty">
      <div className="eyebrow eyebrow-center">{eyebrow}</div>
      <Heading>{title}</Heading>
      <p className="mt-4">{children}</p>
      <div className="ord-empty-actions">
        <Button asChild variant="gold" size="lg">
          <Link href={action.href}>{action.label}</Link>
        </Button>
      </div>
    </div>
  )
}

/**
 * The stored bottle composite, cropped to the label on the glass. Links to
 * the studio when `href` is given.
 */
export function DesignThumb({
  title,
  previewUrl,
  bottleType,
  href,
}: {
  title: string
  previewUrl: string | null
  bottleType: BottleType
  href?: string | null
}) {
  const className = cn("acct-thumb", bottleType === "mcc" && "acct-thumb-mcc")
  const content = previewUrl ? (
    <img src={previewUrl} alt={`Bottle mock-up: ${title}`} loading="lazy" />
  ) : (
    <span className="acct-thumb-empty">Preview to follow</span>
  )

  if (href) {
    return (
      // The card's "Open" link is the accessible way in; the picture repeats it for the pointer.
      <Link href={href} className={className} tabIndex={-1} aria-hidden="true">
        {content}
      </Link>
    )
  }
  return <span className={className}>{content}</span>
}

/** "A vineyard at dusk…" from ai_prompt "botanical: A vineyard at dusk…". */
function paintedDescription(prompt: string | null) {
  if (!prompt) return null
  const index = prompt.indexOf(": ")
  return (index >= 0 ? prompt.slice(index + 2) : prompt).trim() || null
}

/** A design on the account: open it in the studio, duplicate it, download it again. */
export function DesignCard({ design }: { design: AccountDesign }) {
  const [duplicating, setDuplicating] = React.useState(false)
  const titleId = `design-${design.id}-title`
  const painted = design.source_path === "ai"
  const description = painted ? paintedDescription(design.ai_prompt) : null
  const kind = painted ? "Painted by our studio" : design.bottle_type === "mcc" ? "MCC bottle" : "Wine bottle"

  const duplicate = () => {
    if (duplicating) return
    router.post(design.duplicate_url, {}, {
      onStart: () => setDuplicating(true),
      onFinish: () => setDuplicating(false),
    })
  }

  return (
    <article className="acct-design" aria-labelledby={titleId}>
      <DesignThumb
        title={design.title}
        previewUrl={design.preview_url}
        bottleType={design.bottle_type}
        href={design.studio_url}
      />
      <div className="acct-design-body">
        <h3 id={titleId}>{design.title}</h3>
        <span className="acct-meta">{`${kind} · Saved ${formatDay(design.saved_at)}`}</span>
        {description && <span className="acct-prompt">“{description}”</span>}
        {design.enquiry_id !== null && <span className="acct-chip">On enquiry #{design.enquiry_id}</span>}

        <div className="acct-actions">
          <Link href={design.studio_url} className="acct-action">
            Open<span className="sr-only"> {design.title}</span>
          </Link>
          <button type="button" className="acct-action" onClick={duplicate} disabled={duplicating}>
            {duplicating ? "Duplicating…" : "Duplicate"}
            <span className="sr-only"> {design.title}</span>
          </button>
          {design.download_url && (
            // A plain link, never Inertia's router: the server answers with the PNG itself.
            <a href={design.download_url} download={design.download_filename ?? true} className="acct-action">
              Download again<span className="sr-only"> {design.title}</span>
            </a>
          )}
        </div>
      </div>
    </article>
  )
}

export function StatusChip({ status, label }: { status: AccountOrderSummary["status"]; label: string }) {
  return <span className={cn("acct-chip acct-status", `acct-status-${status}`)}>{label}</span>
}

/** "Corporate · Sent 27 Sep 2026 · Date required 12 Jun 2027" */
export function orderMeta(order: AccountOrderSummary) {
  return [
    order.event_type_label,
    `Sent ${formatDay(order.created_at)}`,
    order.event_date ? `Date required ${formatDateOnly(order.event_date)}` : null,
  ]
    .filter(Boolean)
    .join(" · ")
}

/** "120 bottles · Estimate £1,740.00", "About 150 bottles", or nothing. */
function OrderFigures({ order }: { order: AccountOrderSummary }) {
  const parts: React.ReactNode[] = []
  if (order.bottle_count !== null) {
    parts.push(
      order.enquiry_type === "order" ? bottlesText(order.bottle_count) : `About ${bottlesText(order.bottle_count)}`,
    )
  }
  if (order.estimate_pence !== null) {
    parts.push(
      <>
        Estimate <span className="sel-estimate-figure">{formatPence(order.estimate_pence)}</span>
      </>,
    )
  }
  if (order.design_count > 0) {
    parts.push(order.design_count === 1 ? "1 design" : `${order.design_count} designs`)
  }
  if (parts.length === 0) return <span className="acct-order-figures" />

  return (
    <span className="acct-order-figures">
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && " · "}
          {part}
        </React.Fragment>
      ))}
    </span>
  )
}

/** One enquiry in a list: the whole row opens its timeline. */
export function OrderRow({ order }: { order: AccountOrderSummary }) {
  return (
    <li>
      <Link href={order.url} className="acct-order">
        <span className="acct-order-main">
          <span className="acct-order-title">{orderTitle(order)}</span>
          <span className="acct-meta">{orderMeta(order)}</span>
        </span>
        <OrderFigures order={order} />
        <span className="acct-order-side">
          <StatusChip status={order.status} label={order.status_label} />
          <ChevronRight className="acct-order-arrow" aria-hidden="true" />
        </span>
      </Link>
    </li>
  )
}
